// Interactive tour: one fictional heat pump followed through its whole life.
// Everything named here is made up (Halvard, Brightwell Heating, 14 Orchard Lane). The unit is heroUnits[0].
import { capabilities, lifecycle, type Status } from '../content';
import { heroUnits } from '../heroUnits';

const HP = heroUnits[0];

export const UNIT = {
  brand: HP.brand, // Halvard
  model: HP.model, // Halvard AW-9
  short: 'AW-9',
  kind: HP.kind, // Air source heat pump
  serial: HP.serial, // KAW9-26-04182
  installer: HP.partner, // Brightwell Heating
  owner: HP.owner, // 14 Orchard Lane
  nextOwner: 'The new owners',
  handover: '3 Mar 2026',
  warrantyEnd: '3 Mar 2028',
  caseNo: '2041',
  part: HP.part, // Fan motor assembly
  checks: HP.checks,
  tip: HP.answer,
  tipSource: HP.source,
  accent: HP.accent, // #2E6F6A
  product: HP.product,
  scene: HP.scene,
  sceneAlt: HP.sceneAlt,
  domain: 'id.halvard-heat.co.uk',
};

// Photo fallbacks until the generated product shots exist.
export const PHOTO = {
  scene: UNIT.scene, // /images/v2/gen/hp-house-dusk.jpg
  sceneFallback: '/images/v2/gen/heatpump-commission-b.jpg',
  product: UNIT.product, // /images/v2/gen/unit-heatpump.jpg
  productFallback: '/images/v2/gen/heatpump-commission-b.jpg',
  install: '/images/v2/gen/heatpump-commission-b.jpg',
};

export type Persona = 'installer' | 'owner' | 'newowner' | 'public';
export type Surface = 'phone' | 'admin';

export const PERSONA: Record<Persona, { who: string; role: string; switchLine: string }> = {
  installer: { who: 'Brightwell Heating', role: 'Installer', switchLine: 'Scanned by the installer' },
  owner: { who: '14 Orchard Lane', role: 'Homeowner', switchLine: 'Same code, now the homeowner’s' },
  newowner: { who: 'The new owners', role: 'New owner', switchLine: 'Same code, now the new owners’' },
  public: { who: 'Anyone', role: 'Passport view', switchLine: 'Same code, scanned by anyone' },
};

const statusOf = (key: string): Status => capabilities.find((c) => c.key === key)?.status ?? 'live';
const stageStatus = (k: string): Status => lifecycle.stages.find((s) => s.k === k)?.status ?? 'pilot';

export type Step = {
  n: number;
  chapter: string;
  when: string;
  title: string;
  why: string;
  persona: Persona;
  focus: Surface; // where the hotspot is
  hot: string; // hotspot label
  status: Status;
  statusLabel: string;
  stage: string; // unit stage shown in the header
  holder: string; // who has it
  hold?: boolean; // hotspot changes the view but does not advance
  actView?: Surface; // on mobile, show this surface while the action plays
  actMs?: number;
};

export const STEPS: Step[] = [
  {
    n: 1,
    chapter: 'Install',
    when: 'Day 1, at the house',
    title: 'Scan the rating plate.',
    why: 'The code is part of Halvard’s rating plate. The installer scans it with the phone camera, no app, and gets the page for their job.',
    persona: 'installer',
    focus: 'phone',
    hot: 'Scan the code',
    status: statusOf('identity'),
    statusLabel: 'Product identity',
    stage: 'Installing',
    holder: 'Brightwell Heating',
    actMs: 900,
  },
  {
    n: 2,
    chapter: 'Install',
    when: 'Day 1, at the house',
    title: 'Commission it on the phone.',
    why: 'The commissioning checklist for this model is ticked and signed by the installer and the homeowner, then sealed as a PDF on the unit.',
    persona: 'installer',
    focus: 'phone',
    hot: 'Sign and commission',
    status: statusOf('dealers'),
    statusLabel: 'Installer commissioning forms',
    stage: 'Commissioning',
    holder: 'Brightwell Heating',
    actMs: 1100,
  },
  {
    n: 3,
    chapter: 'Install',
    when: 'Day 1, the same minute',
    title: 'Warranty starts today.',
    why: 'Cover starts on the real commissioning date, and Halvard sees the registration land with the installer on it.',
    persona: 'installer',
    focus: 'admin',
    hot: 'New registration',
    status: statusOf('registration'),
    statusLabel: 'Registration & warranty',
    stage: 'Registered',
    holder: '14 Orchard Lane',
    actMs: 350,
  },
  {
    n: 4,
    chapter: 'Living with it',
    when: 'Day 1, that evening',
    title: 'Same code. Now it’s the homeowner’s.',
    why: 'The homeowner scans the same plate and meets their own unit in Halvard’s brand: the setup guide, tips for their home, and service with their installer.',
    persona: 'owner',
    focus: 'phone',
    hot: 'Ask a question',
    status: 'live',
    statusLabel: 'Owner page',
    stage: 'In use',
    holder: '14 Orchard Lane',
    actMs: 350,
  },
  {
    n: 5,
    chapter: 'Living with it',
    when: 'Month 9, first cold snap',
    title: 'Ask, and see the source.',
    why: 'The answer comes from Halvard’s manual for this exact unit, with the page it came from. One tap reaches a person when that is better.',
    persona: 'owner',
    focus: 'phone',
    hot: 'See the part',
    status: statusOf('support'),
    statusLabel: 'Grounded AI support',
    stage: 'In use',
    holder: '14 Orchard Lane',
    actMs: 350,
  },
  {
    n: 6,
    chapter: 'When it goes wrong',
    when: 'Month 9, E21 again',
    title: 'The right part for this serial.',
    why: 'Only parts that fit KAW9-26-04182 are shown. This one is covered, so it becomes a warranty claim for Brightwell to fit.',
    persona: 'owner',
    focus: 'phone',
    hot: 'Claim under warranty',
    status: statusOf('parts'),
    statusLabel: 'Parts & accessories',
    stage: 'In use',
    holder: '14 Orchard Lane',
    actMs: 450,
  },
  {
    n: 7,
    chapter: 'When it goes wrong',
    when: 'Month 9',
    title: 'Report it in a minute.',
    why: 'The serial, photos, warranty, history and installer travel with the report. Nobody reads a serial number off the back of a unit in the rain.',
    persona: 'owner',
    focus: 'phone',
    hot: 'Send to Brightwell Heating',
    status: statusOf('service'),
    statusLabel: 'Service & claims',
    stage: 'In use',
    holder: '14 Orchard Lane',
    actMs: 1000,
  },
  {
    n: 8,
    chapter: 'When it goes wrong',
    when: 'Month 9, at Halvard',
    title: 'The case arrives complete.',
    why: 'Model, serial and installer are already on it. If it is a pattern, a bulletin reaches the exact serials and their homeowners.',
    persona: 'owner',
    focus: 'admin',
    hot: 'Send bulletin',
    status: statusOf('recalls'),
    statusLabel: 'Recalls & bulletins',
    stage: 'Case open',
    holder: '14 Orchard Lane',
    actView: 'phone',
    actMs: 2000,
  },
  {
    n: 9,
    chapter: 'Sold with the house',
    when: 'Year 4, moving out',
    title: 'The house is sold. The record goes with it.',
    why: 'The sellers confirm, the new owners take over the registration, and the warranty and service history stay with the unit. The sellers’ details stay with them.',
    persona: 'owner',
    focus: 'phone',
    hot: 'Confirm transfer',
    status: statusOf('ownership'),
    statusLabel: 'Ownership transfer',
    stage: 'Sold with the house',
    holder: '14 Orchard Lane',
    actMs: 1700,
  },
  {
    n: 10,
    chapter: 'Passport & end of life',
    when: 'Any time',
    title: 'Same code, passport view.',
    why: 'The same plate can carry passport data. An owner sees one view, a recycler sees what they need to handle it safely. Heat pumps have no passport date yet, so today it links the energy label data.',
    persona: 'public',
    focus: 'phone',
    hot: 'Recycler view',
    status: statusOf('dpp'),
    statusLabel: 'Digital Product Passport',
    stage: 'In use',
    holder: 'The new owners',
    hold: true,
  },
  {
    n: 11,
    chapter: 'Passport & end of life',
    when: 'Year 18, replaced',
    title: 'Its next chapter.',
    why: 'When it is replaced, the same page offers Halvard’s take-back route, and the recycler receives the materials data with the unit.',
    persona: 'newowner',
    focus: 'phone',
    hot: 'Book take-back',
    status: stageStatus('Recycle'),
    statusLabel: 'Take-back and recycling',
    stage: 'End of life',
    holder: 'The new owners',
    hold: true,
  },
  {
    n: 12,
    chapter: 'Every unit',
    when: 'Any week, at Halvard',
    title: 'Now, every unit.',
    why: 'Every scan, question and case in this tour lands on Halvard’s boards. Owners keep asking about frost protection and the manuals have no answer. Add one, and the next owner gets it first time.',
    persona: 'newowner',
    focus: 'admin',
    hot: 'Add an answer',
    status: 'live',
    statusLabel: 'Boards and analytics',
    stage: 'In use',
    holder: '3,846 units in the field',
    hold: true,
    actView: 'phone',
  },
];

export const LAST = STEPS.length; // 12
export const END = LAST + 1; // the end screen

export const CHAPTERS = Array.from(new Set(STEPS.map((s) => s.chapter))).map((c) => ({
  name: c,
  steps: STEPS.filter((s) => s.chapter === c).map((s) => s.n),
}));
